import { selectUserState } from '../../reactWrapper/Redux/auth/authSlice';
import { useTypedSelector } from '../../reactWrapper/Redux/utilities';
import { cn } from '../ui-x';

type Nullable = string | null | undefined;

/* The default Cloudinary placeholder is not a real avatar — fall back to
   initials, as the reference does for its own avatars. */
const isDefaultPhoto = (url: Nullable) =>
  !url || url.includes('fyd319ojzgxvzqlhgv9x');

export const initialsOf = (name: Nullable) => {
  const parts = (name ?? '').trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return 'DW';
  if (parts.length === 1) return parts[0].slice(0, 2).toUpperCase();
  return `${parts[0][0]}${parts[1][0]}`.toUpperCase();
};

export const UserAvatar = ({
  photo,
  name,
  className = 'h-7 w-7',
}: {
  photo?: Nullable;
  name?: Nullable;
  className?: string;
}) =>
  isDefaultPhoto(photo) ? (
    <span
      aria-hidden='true'
      className={cn(
        'grid shrink-0 place-items-center rounded-full bg-brand-navy text-[11px] font-bold text-white',
        className
      )}
    >
      {initialsOf(name)}
    </span>
  ) : (
    <img
      src={photo as string}
      alt=''
      aria-hidden='true'
      className={cn('shrink-0 rounded-full object-cover', className)}
    />
  );

/**
 * The header user pill (reference: client.tsx header, far right). Carries the
 * scout state the old header showed as a bare line of text.
 */
const UserInfo = ({ scoutEnabled }: { scoutEnabled: boolean }) => {
  const userState = useTypedSelector(selectUserState);
  const user = userState.user;
  const name = [user?.firstName, user?.lastName].filter(Boolean).join(' ');

  return (
    <span className='flex items-center gap-2 rounded-full border border-border bg-background py-1 pl-1 pr-1 sm:pr-3'>
      <UserAvatar photo={user?.photo} name={name} />
      <span className='hidden min-w-0 sm:block'>
        <span className='block max-w-[9rem] truncate text-xs font-semibold capitalize leading-none text-brand-navy'>
          {name || 'Admin'}
        </span>
        <span className='mt-0.5 block text-[10px] uppercase tracking-wider text-muted-foreground'>
          {scoutEnabled ? 'Scout Free' : 'Scout'}
        </span>
      </span>
    </span>
  );
};

export default UserInfo;
